import { ATTRIBUTES } from '@/constants/product';
import {
	buildOffer,
	buildOfferContext,
	buildReturnPolicy,
	buildShippingDetails,
	getGtin13,
	getVariantSize,
	SCHEMA,
} from '@/linkedData/offer-lkd';
import { HtmlEntities } from '@/utils/HtmlEntities';
import { sanitizeUrl } from '@/utils/millesimaUtils';
import { getAttributeValue, getProductImage } from '@/utils/productUtils';

/**
 * Canonical url of a listing page, built exactly like the one of its canonical link
 * so that both always agree.
 * @param {object} categ The category
 * @param {number} page The current page
 * @param {object} storeConf The store configuration
 * @return {string|undefined} The url
 */
export const getCollectionUrl = (categ, page, storeConf) => {
	if (!categ?.urlKeyword) {
		return undefined;
	}

	const pageNumber = +page;
	const keyword = pageNumber > 1 ? `${categ.urlKeyword}?page=${pageNumber}` : categ.urlKeyword;

	return sanitizeUrl(storeConf?.url ?? '', keyword);
};

/**
 * One entry of the list. With the item displayed on the card, the entry nests the whole
 * Product with its offer, which is what makes the page eligible to the carousel, product
 * snippet and merchant listing results. Without it, the entry stays a plain link.
 * @param {object} entry The product and the item its card displays
 * @param {object} context The offer context
 * @return {object|null} The ListItem, without its position
 */
const buildListItem = ({ product, item }, context) => {
	// Exclusive Cellar prices depend on the logged user
	if (!product?.seoKeyword || product.exclusive_cellar) {
		return null;
	}

	const url = sanitizeUrl(context.host, product.seoKeyword, product.isSpirit);
	const offers = item ? buildOffer(product, item, context) : undefined;

	if (!offers) {
		return { '@type': 'ListItem', url, name: product.productName };
	}

	const producer = getAttributeValue(product, ATTRIBUTES.PRODUCER);
	const size = getVariantSize(item);
	const gtin13 = getGtin13(item);

	return {
		'@type': 'ListItem',
		item: {
			'@type': 'Product',
			name: product.productName,
			url,
			image: getProductImage(product.image, false),
			sku: item.partnumber,
			...(gtin13 && { gtin13 }),
			...(size && { size }),
			...(producer && { brand: { '@type': 'Brand', name: producer } }),
			offers,
		},
	};
};

/**
 * Build the structured data of a page listing products that each link to their own page.
 *
 * The ItemList is a node of its own, at the first level: Google ignores a list nested
 * under CollectionPage.mainEntity. The CollectionPage stays beside it, for the page
 * context only.
 * @param {object} categ The category, as used to build the page meta tags
 * @param {Array} entries The cards actually rendered, in display order, as { product, item }
 * @param {number} page The current page
 * @param {object} storeConf The store configuration
 * @return {Array|string} The JSON-LD nodes, or an empty string when there is nothing to list
 */
const buildLinkedData = (categ, entries, page, storeConf) => {
	const url = getCollectionUrl(categ, page, storeConf);

	// A private category is noindex, and a page without products has no list to describe
	if (!url || categ.isPrivate || !entries?.length || !storeConf) {
		return '';
	}

	const context = buildOfferContext(storeConf);
	const itemListElement = entries
		.map((entry) => buildListItem(entry, context))
		.filter(Boolean)
		// Positions must be contiguous, whatever was skipped
		.map((listItem, index) => ({ '@type': 'ListItem', position: index + 1, ...listItem }));

	if (itemListElement.length === 0) {
		return '';
	}

	const name = HtmlEntities.decode(categ.pageTitle);
	const description = HtmlEntities.decode(categ.metaDescription);

	const nodes = [
		{
			'@context': SCHEMA,
			'@type': 'ItemList',
			url,
			numberOfItems: itemListElement.length,
			itemListElement,
		},
		{
			'@context': SCHEMA,
			'@type': 'CollectionPage',
			...(name && { name }),
			...(description && { description }),
			url,
		},
	];

	// The offers reference the policies by @id, so they must exist on the page
	if (itemListElement.some(({ item }) => item?.offers)) {
		nodes.push(buildShippingDetails(storeConf), buildReturnPolicy(storeConf));
	}

	return nodes;
};

export default buildLinkedData;
